<template>
  <div class="item">
    <div class="circle"></div>
    <div class="plot-item-content">
      <textarea class="plot-card" v-model="inputValue" :placeholder="$t('plotEditPage.plotItemDetail.placeholder')" maxlength="2500" />
      <div v-if="isPC" class="plot-item-footer">
        <div v-if="(!child || !childPlotName) && !isInitial" class="create-child-plot" @click="createChildPlot">
          <link-icon class="icon mr-4" :size="16" />{{ $t('plotEditPage.plotItemDetail.createChildPlot') }}
        </div>
        <div v-else-if="!isInitial" class="create-child-plot">
          <div class="link-child">
            <router-link :to="{ name: 'plotEdit', params: { novelId, plotId: child } }"
              ><link-icon class="icon mr-4" :size="16" /><span>{{ $t('plotEditPage.plotItemDetail.childPlot', { name: childPlotName }) }}</span></router-link
            >
          </div>
          <div class="cancel" @click="cancelLink">{{ $t('plotEditPage.plotItemDetail.unlinkChildPlot') }}</div>
        </div>
        <div class="menu-parent">
          <dots-horizontal-circle-icon class="icon light-gray" @click="clickMenu" v-if="!isInitial" />
          <!-- TODO  onClickOutsideがちゃんと効いていない-->
          <div v-if="displayMenu" class="menu" v-click-outside="onClickOutside" @click="deleteItem">
            {{ $t('plotEditPage.plotItemDetail.deletePlot') }}
          </div>
        </div>
      </div>
    </div>
    <drag-horizontal-variant-icon class="icon handle-disp" />
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import DragHorizontalVariantIcon from "icons/DragHorizontalVariant.vue";
import DotsHorizontalCircleIcon from "icons/DotsHorizontalCircle.vue";
import LinkIcon from "icons/LinkVariant.vue";
import { Dialog, isBilling } from "@/lib/utils";
import CreateChildPlotDialog, { CreateChildPlotDialogProps } from "@/components/ui/CreateChildPlotDialog.vue";
import BreakingChangeConfirm, { BreakingChangeConfirmProps } from "@/components/ui/dialogs/BreakingChangeConfirm.vue";
import { SubPlot } from "@/lib/models/plot";
import { ChildPlotItem } from "@/lib/plot";
import isMobile from "ismobilejs";

const ClickOutside = require("vue-click-outside");

interface Props {
  novelId: string;
  plotId: string;
  value: string;
  isInitial: boolean;
  // 子プロットのID
  child: string;
}

interface Data {
  displayMenu: boolean;
}

interface Computed {
  inputValue: string;
  childPlotName: string;
  isPC: boolean;
}

interface Methods {
  onClickOutside(): void;
  clickMenu(): void;
  createChildPlot(): void;
  deleteItem(e: MouseEvent): void;
  cancelLink(): void;
}

export default Vue.extend<Data, Methods, Computed, Props>({
  directives: { ClickOutside },
  props: {
    novelId: String,
    plotId: String,
    value: String,
    isInitial: Boolean,
    child: String,
  },
  components: {
    DragHorizontalVariantIcon,
    DotsHorizontalCircleIcon,
    LinkIcon,
  },
  computed: {
    inputValue: {
      get() {
        return this.value;
      },
      set(newValue) {
        this.$emit("input", newValue);
      },
    },
    childPlotName() {
      if (!this.child) return "";
      const subPlots = this.$store.getters["plotModule/subPlots"];
      const subPlot = subPlots.filter((i: SubPlot) => i.plotId === this.child);
      if (subPlot.length > 0) return subPlot[0].name;
      return "";
    },
    isPC() {
      return !isMobile().any;
    },
  },
  data() {
    return {
      displayMenu: false,
    };
  },
  methods: {
    onClickOutside() {
      // this.displayMenu = false;
    },
    clickMenu() {
      this.displayMenu = !this.displayMenu;
    },
    async createChildPlot() {
      const { $store, plotId } = this;
      const hasLimit = !(await isBilling($store));
      const subPlots = $store.getters["plotModule/subPlots"];

      const childPlotDialog = new Dialog(CreateChildPlotDialog);
      const data: CreateChildPlotDialogProps = {
        plotList: subPlots.filter((i: SubPlot) => i.plotId !== plotId),
        positiveCallback: (value: ChildPlotItem) => {
          this.$emit("linkPlot", value);
        },
        hasLimit,
      };

      childPlotDialog.show(data);
    },
    async cancelLink() {
      const confirmDialog = new Dialog(BreakingChangeConfirm);
      const data: BreakingChangeConfirmProps = {
        title: this.$t('plotEditPage.plotItemDetail.unlinkChildPlotTitle'),
        content: this.$t('plotEditPage.plotItemDetail.unlinkChildPlotContent'),
        change: this.$t('plotEditPage.plotItemDetail.unlinkButton'),
      };
      const result = await confirmDialog.show(data);

      if (!result) {
        return;
      }
      this.$emit("cancelLink");
    },
    async deleteItem(e) {
      this.displayMenu = false;
      const confirmDialog = new Dialog(BreakingChangeConfirm);
      const data: BreakingChangeConfirmProps = {
        title: this.$t('plotEditPage.plotItemDetail.deletePlotTitle'),
        content: this.$t('plotEditPage.plotItemDetail.deletePlotContent'),
        change: this.$t('plotEditPage.plotItemDetail.deleteButton'),
      };
      const result = await confirmDialog.show(data);

      if (!result) {
        return;
      }
      this.$emit("deleteItem", e);
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.circle {
  position: absolute;
  display: block;
  background: url("../../assets/img/plot/plot_circle.png") no-repeat;
  top: 0;
  left: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  background-size: 15px auto;
  background-position: 15px 50%; /* 左上から右に20px, 下に30px */
}

  .icon {
    display: flex;
    cursor: pointer;
    z-index: 5;

    svg {
      display: block;
      margin: 0 auto;
    }

    &.light-gray {
      svg {
        fill: var(--color-text-low);
      }
    }
  }

  .item {
    position: relative;
    padding: 5px 15px 5px 40px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    text-decoration: none;
    border-bottom: 1px solid var(--color-border);
    background: url("../../assets/img/plot/plot_dot.png") repeat-y;
    background-position: 20px 0px; /* 左上から右に20px, 下に30px */

  .plot-item-content {
    position: relative;
    box-sizing: border-box;
    vertical-align: middle;
    width: 100%;
    background: var(--color-background-normal);
    color: var(--color-text-normal);
    border-right: 3px solid var(--color-border);
    padding-right: 6px;
    margin-right: 10px;

    textarea {
      flex-grow: 1;
      border-radius: var(--radius-xs);
      border: solid 1px var(--color-border);
      padding: 10px;
      height: 80px;
      background-color: var(--color-background-normal);
      color: var(--color-text-normal);
    }

    .item-handle {
      width: 100%;
      height: 19px;
    }

    /* 掴んで移動可能な領域 */
    .handle-disp {
      display: none;
      position: absolute;

      background-color: transparent;
      transition: background-color 0.8s ease-in-out;
    }

    &:hover {
      .handle-disp {
        display: block;
        background-color: var(--color-surface-hover);

        animation-name: plot-item-fadeIn;
        animation-duration: 0.5s;
      }
    }

    .plot-item-footer {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0 10px 5px 10px;

      .menu-parent {
        position: relative;
      }

      .menu {
        position: absolute;
        right: 0;
        top: 24px;
        width: 190px;
        font-size: var(--font-size-sm);
        color: var(--color-warning);
        border: 1px solid var(--color-border);
        padding: 5px;
        background: var(--color-background-normal);
        cursor: pointer;
        z-index: 10;
      }

      .create-child-plot {
        display: flex;
        justify-content: flex-start;
        align-items: center;
        cursor: pointer;

        .link-child {
          a {
            display: flex;
            align-items: center;
            height: 100%;
            justify-content: flex-start;
            color: var(--color-primary);
            text-decoration: none;
          }

          .icon {
            margin-right: 4px;
          }
        }

        .cancel {
          margin-left: 10px;
          padding-left: 10px;
          border-left: 1px solid var(--color-border);
          cursor: pointer;
          color: var(--color-text-normal);
        }
      }
    }
  }

  .delete-button {
    position: absolute;
    width: 19px;
    height: 19px;
    padding: 0;
    font-size: var(--font-size-sm);
    text-align: center;
    vertical-align: middle;
    border: none;
    background-color: var(--color-surface-muted);
    color: var(--color-text-normal);
    right: 0;
    top: 0;
    cursor: pointer;

    @include hover-overlay;
  }

  .plot-card {
    font-size: 16.1px;
    width: calc(100% - 20px);
    height: calc(100% - 35px);
    resize: none;
    margin: 10px 10px 6px;
    padding: 0;
    box-sizing: border-box;
    border: none;
    outline: none;
    background-color: var(--color-background-normal);
    color: var(--color-text-normal);
  }

  &:last-child {
    .plot-item-content {
      cursor: default;
    }
  }
}

.mr-4 {
  margin-right: 4px;
}

@keyframes plot-item-fadeIn {
  0% {
    opacity: 0;
  }
  30% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}
</style>
